import React, { useEffect, useContext } from 'react';
import { Box, Button, TextField, Grid, Typography, IconButton, MenuItem, Autocomplete, Tooltip } from '@mui/material';
import { Add, HighlightOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Plus } from '../../../assets';
import { ReportsBuilderContext, ReportBuilderValues } from '../context/ReportsBuilderContext';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import { ReportFilterGroupFilterDto, ReportFilterGroupDto } from '../../../app/services/api/generated';
import { RtvReason, returnToVendorReasonsTranslationMap, parseRtvReason } from '../../../pages/CreditDetails/enums/return-to-vendor-reason';
import { Reason, creditRequestReasonsTranslationMap, parseReason } from '../../../pages/CreditDetails/enums/reason';

const Filters = () => {
  const { 
    t,
    selectedDataGroup,
    addFilterGroup,
    addFilter,
    removeFilter,
    handleFilterChange,
    removeFilterGroup,
    handleFilterLogicalOperatorChange,
    handleGroupLogicalOperatorChange,
    filterGroups,
    logicalOperator,
    filterMetrics, 
    filterGroupLinks, 
    loading,
    allCategories,
    transactionTypes,
    reasons,
    filterOperatorsMap,
    ordersType,
    groupReasons, 
    invalidFilters,
    isMobile, 
    selectedStore, 
    columns,
    columnMetrics,

  } = useContext(ReportsBuilderContext) as ReportBuilderValues;

  const theme = useTheme();

  useEffect(() => {
    addFilterGroup();
  }, []);
   
  const isReasonForCreditRequestSelectable = (reason: string | Reason): boolean => {
    reason = parseReason(reason);
    if (selectedStore?.franchiseName === 'AMREST') {
      return reason === Reason.SHORTAGE || reason === Reason.OVERAGE;
    } else if (selectedStore?.franchiseName === 'AmRest Poland') {
      return reason === Reason.NO_PRODUCT_IN_DELIVERY || reason === Reason.EXCESS_INVOICE_ADJUSTMENT;
    }
    return reason !== Reason.OUT_OF_TEMPERATURE && reason !== Reason.OVERAGE;
  };

  const isReasonForReturnToVendorSelectable = (reason: string | RtvReason): RtvReason => {
    reason = parseRtvReason(reason);
    return reason;
  };

  const getFieldError  = (groupId: number, filterId: number | undefined, field: string) => {
    return invalidFilters.some(
      (invalid) => invalid.groupId === groupId && invalid.filterId === filterId && invalid.field === field,
    );
  };

  const getFieldErrorMessage = (groupId: number, filterId: number | undefined, field: string): string | undefined => {
    const error = invalidFilters.find(
      (invalid) => invalid.groupId === groupId && invalid.filterId === filterId && invalid.field === field,
    );
    return error?.message;
  };
 


 
  const hasSelectedFields = columns?.some(col => col.metricId !== 0 && col.metricId !== undefined);

  const isAddFilterGroupEnabled = () => {
    // Must have at least one selected field
    if (!hasSelectedFields) return false;
    
    return filterGroups?.every(group =>
    group.reportFilterGroupFilters!.length >= 2 &&
    group.reportFilterGroupFilters!.every(filterGroupFilter => {
      const { filterMetricId, operatorId, value } = filterGroupFilter.filter || {};
      return !!filterMetricId && !!operatorId && !!value;  
    }),
    );
  };

  const availableFilterMetrics = React.useMemo(() => {
    if (!filterMetrics) return [];
    
    // Get the metric IDs of selected columns
    const selectedColumnMetricIds = columns
      ?.filter(col => col.metricId !== 0 && col.metricId !== undefined)
      .map(col => col.metricId) ?? [];
    
    // Get the names of selected columns from columnMetrics
    const selectedColumnNames = selectedColumnMetricIds
      .map(id => columnMetrics?.find(cm => cm.id === id)?.name)
      .filter((name): name is string => !!name);
    
    // Filter: include filters matching selected column names OR default filters
    const filtered = filterMetrics.filter(metric => {
      // Include if metric is a default field (available for all data groups)
      if (metric.isDefaultField) {
        return true;
      }
      // Include if metric name matches a selected column name
      return selectedColumnNames.some(colName => 
        colName.toLowerCase() === metric.name?.toLowerCase(),
      );
    });
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filterMetrics, columns, columnMetrics]); 

  const renderValueInput = (filterGroupFilter: ReportFilterGroupFilterDto, group: ReportFilterGroupDto) => {  
    const metric = filterMetrics?.find(metric => metric.id === filterGroupFilter.filter!.filterMetricId); 

   
    const handleDecimalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
  
      let value = e.target.value;

      if (metric?.fieldType === 'decimal' || metric?.fieldType === 'int') {
        const allowNegative = (metric?.name === 'Transaction Quantity' || metric?.name == 'Variance - Cost Price' || metric?.name == 'Variance - Quantity') ;
        value = value.replace(allowNegative ? /[^0-9.-]/g : /[^0-9.]/g,'');
    
        if (allowNegative && value.includes('-')) {
          value = '-' + value.replace(/-/g, '');  
        }
    
        const parts = value.split('.');
        if (parts.length > 2) {
          value = parts[0] + '.' + parts.slice(1).join('');
        }
    
        if (parts.length === 2) {
          value = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }

      handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', value);
   
      setTimeout(() => {
        e.target.setSelectionRange(start, end);
      }, 0);
    };

    const isValueInvalid = getFieldError(group.id!, filterGroupFilter.filter?.uiId, 'Value');
    const valueErrorMessage = getFieldErrorMessage(group.id!, filterGroupFilter.filter?.uiId, 'Value');

    if (metric?.name === 'Product Category') {
      return ( 
        <Autocomplete
          multiple
          fullWidth
          options={allCategories}
          getOptionLabel={(option) => option.primaryCategoryName ?? ''}
          value={
   filterGroupFilter.filter!.value
     ? allCategories.filter((category) =>
       category.primaryCategoryName && 
        filterGroupFilter.filter!.value?.split(',').includes(category.primaryCategoryName), 
     )
     : []
          }
          onChange={(event, newValue) =>
            handleFilterChange(
              group.id,
      filterGroupFilter.filter!.uiId,
      'value',
      newValue.map((item) => item.primaryCategoryName).join(','),
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('value')}
              error={isValueInvalid}
              helperText={isValueInvalid ? valueErrorMessage : ''}
            />
          )}
        /> 
      );
    } 
    else if (metric?.name === 'Transaction Reason') {
      const groupId = group?.id; 
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        > 
          {[
            ...(groupReasons[groupId!] || reasons),
            { id: 'purchase-order-adj', name: 'Purchase Order Adjustment' },
          ].map((reason) => (
            <MenuItem key={reason.id}
              value={reason.name}>
              {reason.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    else if (metric?.name === 'Transaction Type') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        >
          {transactionTypes.map((transaction) => (
            <MenuItem key={transaction.id}
              value={transaction.type}>
              {transaction.type}
            </MenuItem>
          ))}
        </TextField>
      ); 
    } else if (metric?.name === 'Order Type') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        >
          {ordersType?.name?.map((name) => (
            <MenuItem key={name}
              value={name}>
              {name}
            </MenuItem>
          ))} 
        </TextField>
      );
    }  
    else if (metric?.name === 'Transaction Source') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        > 
          <MenuItem value="manual">{t('manual')}</MenuItem>
          <MenuItem value="pos">{t('pos')}</MenuItem>
          <MenuItem value="system">{t('system')}</MenuItem>
        </TextField>
      );
    }
    else if (metric?.name === 'Credit Request Reason') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        >
          {Object.entries(creditRequestReasonsTranslationMap)
            .filter(([reasonKey]) =>
              isReasonForCreditRequestSelectable(reasonKey),
            )
            .map(([reasonKey, translation]) => (
              <MenuItem key={reasonKey}
                value={reasonKey}>
                {t(translation)}
              </MenuItem>
            ))}
        </TextField>
      );
    }  
    else if (metric?.name === 'Return To Vendor Reason') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value)}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        >
          {Object.entries(returnToVendorReasonsTranslationMap)
            .filter(([reasonKey]) =>
              isReasonForReturnToVendorSelectable(reasonKey),
            )
            .map(([reasonKey, translation]) => (
              <MenuItem key={reasonKey}
                value={reasonKey}>
                {t(translation)}
              </MenuItem>
            ))}
        </TextField>
      );
    }  
    else if (metric?.fieldType === 'boolean') {
      return (
        <TextField
          select
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) =>
            handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value )
          }
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
        >
          <MenuItem value="true">{t('true')}</MenuItem>
          <MenuItem
            value="false"
            style={{ display: metric?.name === 'Stock Count Date' ? 'none' : 'block' }}

          >
            {t('false')}
          </MenuItem>
        </TextField> 
      );
    } else if (metric?.fieldType === 'int' || metric?.fieldType === 'decimal') {
      return (
        <TextField
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={handleDecimalChange}
          fullWidth
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
          type="text"  
         
        />

      );
    }  else {
      return (
        <TextField
          label={t('value')}
          value={filterGroupFilter.filter!.value ?? ''}
          onChange={(e) => { 
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
        
            handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'value', e.target.value);
         
            setTimeout(() => {
              e.target.setSelectionRange(start, end);
            }, 0);
          }}
          error={isValueInvalid}
          helperText={isValueInvalid ? valueErrorMessage : ''}
          fullWidth
        />
      );
    }
  };
 
  const getColumnWidth = (filterIndex: number): number => {
    return filterIndex > 0 ? 3 : 4;
  };

  return (
    <>
      {loading ? (
        <Grid item
          xs={12}>
          <LoadingWheel />
        </Grid>
      ) : (
        <>
          <Box
            sx={{
              border: '1px solid',
              borderRadius: '4px',
              borderColor: theme.palette.custom.gray[200],
              p: 3,
              mt: 3,
              opacity: selectedDataGroup ? 1 : 0.5,
              pointerEvents: selectedDataGroup ? 'auto' : 'none',
            }}
          >
            <Grid container>
              <Grid item
                xs={6}>
                <Tooltip
                  title="Filters to refine the data presented in the output report. Group filters together in Filter Groups for greater control."
                  arrow
                  enterDelay={500}
                >
                  <Typography
                    sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}
                  >
                    {t('filters')}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item
                xs={6}
                sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <Tooltip title="Add a new filter group."
                  arrow
                  enterDelay={500}>
                  <Button
                    size="lg"
                    sx={{ mt: 7 }}
                    variant="primary"
                    onClick={addFilterGroup}
                    disabled={!isAddFilterGroupEnabled()}
                  >
                    <Plus />
                    {t('addFilterGroup')}
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
            {filterGroups?.map((group, groupIndex) => (
              <Box key={group.id} >
                {groupIndex > 0 && (
                  <Box sx={{ mt: 3, mb: 1 }}>
                    {(() => {
                      const link = filterGroupLinks?.find(link => link.group2Id === group.id);
                      const isGroupLogicalOperatorInvalid =
      getFieldError(group.id!, undefined, 'GroupLogicalOperator');
 
                      return (
                        <Box sx={{ width: isMobile ? '100%' : '16.666%' }}>
                          <TextField
                            select 
                            label={t('logicalOperatorGroup')}
                            value={link?.logicalOperatorId ?? ''}
                            onChange={(e) => handleGroupLogicalOperatorChange(group.id, e.target.value)}
                            fullWidth
                            error={isGroupLogicalOperatorInvalid}
                            helperText={isGroupLogicalOperatorInvalid ? getFieldErrorMessage(group.id!, undefined, 'GroupLogicalOperator') : ''}
                            sx={{ mb: 2 }}   
                            SelectProps={{
                              renderValue: () => {
                                const selectedMetric = logicalOperator?.find(a => a.id === link?.logicalOperatorId);
                                return (
                                  <Tooltip
                                    title={selectedMetric?.description ?? '' }
                                    enterDelay={300}
                                    leaveDelay={100}
                                    disableInteractive
                                  >
                                    <span>{selectedMetric?.name ?? ''}</span>
                                  </Tooltip>
                                );
                              },
                            }}
                          >
                            {logicalOperator?.map((option) => (
                              <MenuItem key={option.id}
                                value={option.id?.toString()}>
                                {option.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      );
                    })()}
                  </Box>
                )}
                <Grid item>
                  <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                    {t('group')} {groupIndex + 1}
                    {groupIndex > 0 && (
                      <IconButton onClick={() => removeFilterGroup(group.id)}
                        sx={{ ml: 2, padding: 0, transform: 'translateY(-1.5px)' }}>
                        <HighlightOff />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                {group.reportFilterGroupFilters?.map((filterGroupFilter, filterIndex) => (
                  
                  <Grid container
                    spacing={2}
                    alignItems="flex-start"
                    key={filterGroupFilter.filter!.uiId} 
                    sx={{  mb: filterIndex > 0 ? 2 : 0, mt: 3,
                      border: isMobile ? '1px solid #ddd' : 'none',  
                      borderRadius: '8px',  
                      marginBottom: isMobile ? '12px' : '0', 
                      padding: isMobile ? '8px' : '0',
                    }}>
                    {filterIndex > 0 && (
                      
                      <Grid item
                        xs={isMobile ? 12 : 2}>
                        {(() => {
                          const isLogicalOperatorInvalid =
      getFieldError(group.id!, filterGroupFilter.filter?.uiId, 'LogicalOperator');
 
                          return ( 
                            <Box sx={{ width: '100%' }}>
                              <TextField
                                select
                                label={t('logicalOperator')}
                                value={filterGroupFilter.logicalOperatorId ?? ''}
                                onChange={(e) =>
                                  handleFilterLogicalOperatorChange(group.id, filterGroupFilter.filter?.uiId, e.target.value)
                                }
                                fullWidth
                                error={isLogicalOperatorInvalid}
                                helperText={isLogicalOperatorInvalid ? getFieldErrorMessage(group.id!, filterGroupFilter.filter?.uiId, 'LogicalOperator') : ''}
                                SelectProps={{
                                  renderValue: () => {
                                    const selectedMetric = logicalOperator?.find(a => a.id === filterGroupFilter.logicalOperatorId);
                                    return (
                                      <Tooltip
                                        title={selectedMetric?.description ?? '' }
                                        enterDelay={300}
                                        leaveDelay={100}
                                        disableInteractive
                                      >
                                        <span>{selectedMetric?.name ?? ''}</span>
                                      </Tooltip>
                                    );
                                  },
                                }}
                                sx={{ mb: 2 }}
                              >
                                {logicalOperator?.map((option) => (
                                  <MenuItem key={option.id}
                                    value={option.id?.toString()}>
                                    {option.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box> 
                          );
                        })()}
                      </Grid>

                    )}
        
                    <Grid item
                      xs={isMobile ? 12 : 3}> 
                      <Box sx={{ width: '100%' }}>
                        <TextField
                          select
                          label={t('metric')}
                          value={(filterGroupFilter.filter!.filterMetricId !== undefined  && filterGroupFilter.filter!.filterMetricId !== 0 )? filterGroupFilter.filter!.filterMetricId : ''}
                          onChange={(e) =>
                            handleFilterChange(
                              group.id,
        filterGroupFilter.filter!.uiId,
        'filterMetricId',
        e.target.value,
                            )
                          }
                          fullWidth
                          error={getFieldError(group.id!, filterGroupFilter.filter!.uiId, 'Metric')}
                          helperText={getFieldError(group.id!, filterGroupFilter.filter!.uiId, 'Metric') ? getFieldErrorMessage(group.id!, filterGroupFilter.filter!.uiId, 'Metric') : ''}
                          SelectProps={{
                            renderValue: (value) => {
                              const selectedMetric = filterMetrics?.find(m => m.id === value);
                              return (
                                <Tooltip
                                  title={selectedMetric?.description ?? ''}
                                  enterDelay={300}
                                  leaveDelay={100}
                                  disableInteractive
                                >
                                  <span>{selectedMetric?.name ?? ''}</span>
                                </Tooltip>
                              );
                            },
                          }}
                        >
                          {availableFilterMetrics?.map((option) => (
                            <MenuItem key={option.id}
                              value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box> 
                    </Grid> 
                    <Grid item
                      xs={isMobile ? 12 : getColumnWidth(filterIndex)}
                      sx={{
                        pt: isMobile ? '10px !important' : '0px',
                      }}
                    >
                      <TextField 
                        select
                        label={t('operator')}
                        value={filterGroupFilter.filter!.operatorId ? filterGroupFilter.filter!.operatorId : ''}
                        onChange={(e) => handleFilterChange(group.id, filterGroupFilter.filter!.uiId, 'operatorId', e.target.value)}
                        fullWidth
                        error={getFieldError(group.id!, filterGroupFilter.filter!.uiId, 'Operator')}
                        helperText={getFieldError(group.id!, filterGroupFilter.filter!.uiId, 'Operator') ? getFieldErrorMessage(group.id!, filterGroupFilter.filter!.uiId, 'Operator') : ''}
                      >
                        {filterGroupFilter.filter?.filterMetricId && (
                          filterOperatorsMap
                            .get(filterGroupFilter.filter.filterMetricId)
                            ?.filter((option) => {
                              const metric = filterMetrics?.find(
                                (m) => m.id === filterGroupFilter.filter!.filterMetricId,
                              );

                              const isStockCountDate = metric?.name === 'Stock Count Date';  
                              return !(isStockCountDate && option.name?.toLowerCase()?.includes('!='));
                            })
                            .map((option) => (
                              <MenuItem key={option.id}
                                value={option.id}>
                                {option.description}
                              </MenuItem>
                            ))
                        )} 
                      </TextField> 
                    </Grid> 
                    <Grid item
                      xs={isMobile ? 10 : getColumnWidth(filterIndex)}
                      sx={{
                        pt: isMobile ? '10px !important' : '0px', 
                      }}>
                      {renderValueInput(filterGroupFilter, group)}
                    </Grid>
                    <Grid item
                      xs={1}>
                      <IconButton onClick={() => removeFilter(group.id, filterGroupFilter.filter!.uiId!)}
                        sx={{ mt: 1 }}>
                        <HighlightOff  />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 2 }}>
                  <Tooltip title="Add a new filter to the filter group."
                    arrow
                    enterDelay={500}>
                    <Button
                      variant="primary"
                      size="lg"
                      sx={{ mt: 3, mb: 3 }}
                      startIcon={<Add />}
                      onClick={() => addFilter(group.id)}
                    >
                      {t('addFilters')}
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );
};

export default Filters;
